const API_BASE = 'https://api.postcodes.io'

const coordinatesCache = new Map()

const ALT_GEOCODER = 'https://nominatim.openstreetmap.org/search'

const cleanQuery = (value = '') => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')

const isFiniteNumber = (value) => Number.isFinite(value) && Math.abs(value) <= 180

const fetchCoordinates = async (endpoint) => {
  const response = await fetch(`${API_BASE}/${endpoint}`)
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || `Postcode lookup failed (${response.status})`)
  }
  const payload = await response.json().catch(() => null)
  const result = payload?.result
  if (!result) return null
  const latitude = Number(result.latitude)
  const longitude = Number(result.longitude)
  if (!isFiniteNumber(latitude) || !isFiniteNumber(longitude)) {
    return null
  }
  return { latitude, longitude }
}

export const lookupCoordinates = async (query) => {
  const normalized = cleanQuery(query)
  if (!normalized) return null
  if (coordinatesCache.has(normalized)) {
    return coordinatesCache.get(normalized)
  }
  const endpoints =
    normalized.length > 4
      ? [`postcodes/${normalized}`, `outcodes/${normalized}`]
      : [`outcodes/${normalized}`, `postcodes/${normalized}`]

  for (const endpoint of endpoints) {
    try {
      const coords = await fetchCoordinates(endpoint)
      if (coords) {
        coordinatesCache.set(normalized, coords)
        return coords
      }
    } catch {
      // Try the next endpoint before surfacing an error
    }
  }

  coordinatesCache.set(normalized, null)
  return null
}

export const geocodePlace = async (query) => {
  const cacheKey = `place:${query.toLowerCase().trim()}`
  if (coordinatesCache.has(cacheKey)) {
    return coordinatesCache.get(cacheKey)
  }
  const url = new URL(ALT_GEOCODER)
  url.searchParams.set('format', 'json')
  url.searchParams.set('limit', '1')
  url.searchParams.set('countrycodes', 'gb')
  url.searchParams.set('q', query)
  const response = await fetch(url.toString(), {
    headers: {
      'Accept-Language': 'en-GB',
    },
  })
  if (!response.ok) {
    coordinatesCache.set(cacheKey, null)
    return null
  }
  const payload = await response.json().catch(() => [])
  const first = payload?.[0]
  if (!first) {
    coordinatesCache.set(cacheKey, null)
    return null
  }
  const latitude = Number(first.lat)
  const longitude = Number(first.lon)
  if (!isFiniteNumber(latitude) || !isFiniteNumber(longitude)) {
    coordinatesCache.set(cacheKey, null)
    return null
  }
  const coords = { latitude, longitude }
  coordinatesCache.set(cacheKey, coords)
  return coords
}

export const extractOutcode = (value = '') => {
  const normalized = value.toUpperCase()
  const match = normalized.match(/\b[A-Z]{1,2}\d[A-Z\d]?\b/)
  return match ? match[0].replace(/\s+/g, '') : ''
}
